/**
 * Ventana de período del cierre de mes (fecha de corte configurable).
 *
 * El cierre se sigue identificando/etiquetando por `(month, year)` (ej. "Septiembre
 * 2026"), pero su PERÍODO real es una ventana de fechas `[start, end]` que el admin
 * define con un "día de corte" por edificio. Ej: día de corte 5 → el cierre de
 * septiembre cubre del 05/09 al 05/10.
 *
 * Convención de pertenencia: un movimiento con fecha `d` pertenece a la ventana si
 * `start < d <= end` (INICIO EXCLUSIVO, FIN INCLUSIVO). Así las ventanas de meses
 * consecutivos son contiguas y NO se solapan (el `start` de un mes = el `end` del
 * anterior): un pago del 05/09 cae en agosto, uno del 05/10 en septiembre.
 *
 * IMPORTANTE: `isInWindow` está pensado para ventanas de corte. Los edificios SIN
 * día de corte usan el mes calendario y los motores siguen filtrando con `isInMonth`
 * (comportamiento idéntico al actual). `resolveCierreWindow` devuelve para el caso
 * calendario `[1 del mes, último del mes]` solo a fines de etiqueta/preview.
 *
 * Helpers PUROS (sin Firebase) → testeables.
 */

// Parsea cualquier formato de fecha a Date. Ancla "YYYY-MM-DD" a mediodía local
// para evitar el corrimiento de un día en UY (UTC-3). Espeja el patrón de dates.js.
function toDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val.toDate === "function") return val.toDate();
  if (val.seconds != null) return new Date(val.seconds * 1000);
  if (typeof val === "string") {
    const s = val.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(val) ? val + "T12:00:00" : val;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "number") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function isoOf(val) {
  const d = toDate(val);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isoYMD(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function lastDayOfMonth(year, month) {
  // month 1-12
  return new Date(year, month, 0).getDate();
}

/** Normaliza el día de corte a un entero [1..28], o null si no está seteado/es inválido. */
export function normalizeCutoffDay(diaCorte) {
  if (diaCorte === "" || diaCorte == null) return null;
  const n = Math.floor(Number(diaCorte));
  if (isNaN(n) || n < 1) return null;
  return Math.min(28, n);
}

/**
 * Resuelve la ventana `{ start, end }` (ISO "YYYY-MM-DD") del cierre etiquetado (month, year).
 * Prioridad:
 *   1. `cierreDoc.periodStart`/`periodEnd` explícitos (cierre ya creado o override manual).
 *   2. `diaCorte`: start = día D del mes M; end = día D del mes M+1 (roll de año en diciembre).
 *   3. Mes calendario (fallback backward-compatible): start = 1 del mes, end = último del mes.
 *
 * @param {number} month 1-12
 * @param {number} year
 * @param {{ diaCorte?: number|string|null, cierreDoc?: {periodStart?: any, periodEnd?: any}|null }} [opts]
 * @returns {{ start: string, end: string }}
 */
export function resolveCierreWindow(month, year, { diaCorte = null, cierreDoc = null } = {}) {
  const m = Number(month);
  const y = Number(year);

  if (cierreDoc && cierreDoc.periodStart && cierreDoc.periodEnd) {
    const s = isoOf(cierreDoc.periodStart);
    const e = isoOf(cierreDoc.periodEnd);
    if (s && e) return { start: s, end: e };
  }

  const D = normalizeCutoffDay(diaCorte);
  if (D) {
    const start = isoYMD(y, m, Math.min(D, lastDayOfMonth(y, m)));
    const nm = m === 12 ? 1 : m + 1;
    const ny = m === 12 ? y + 1 : y;
    const end = isoYMD(ny, nm, Math.min(D, lastDayOfMonth(ny, nm)));
    return { start, end };
  }

  // Fallback calendario (solo etiqueta/preview; la pertenencia calendario usa isInMonth).
  return { start: isoYMD(y, m, 1), end: isoYMD(y, m, lastDayOfMonth(y, m)) };
}

/**
 * Selección de gastos liquidables por un cierre (lógica pura, testeable). Un gasto
 * entra si: está PAGADO, su `paidDate` es <= al FIN de la ventana (`endDate`), y NO
 * fue ya liquidado por otro cierre (su id no está en `appliedExpenseIds`). Este
 * último gate es el invariante ANTI DOBLE-CONTEO: un gasto ya incluido en un cierre
 * no vuelve a entrar hasta que ese cierre se elimine (se lo quita de appliedExpenseIds).
 * No hay tope inferior: el gate relacional garantiza que nada se pierda ni se duplique.
 *
 * @param {Array<{id:any, paid?:boolean, paidDate?:any}>} expenses
 * @param {{ appliedExpenseIds?: Set<any>|Array<any>, endDate: any }} opts
 * @returns {Array} los gastos seleccionables
 */
export function selectLiquidableExpenses(expenses, { appliedExpenseIds, endDate } = {}) {
  const applied = appliedExpenseIds instanceof Set ? appliedExpenseIds : new Set(appliedExpenseIds || []);
  const end = endDate instanceof Date
    ? endDate
    : new Date(String(endDate).slice(0, 10) + "T23:59:59");
  if (isNaN(end.getTime())) return [];
  return (expenses || []).filter((exp) => {
    if (!exp || !exp.paid) return false;
    if (applied.has(exp.id)) return false; // ya liquidado por otro cierre no eliminado
    const d = toDate(exp.paidDate);
    if (!d) return false;
    return d.getTime() <= end.getTime();
  });
}

/**
 * Ids de gastos que ya liquidaron OTROS cierres del edificio (el `appliedExpenseIds`
 * de selectLiquidableExpenses). Lógica pura compartida por el cierre y el informe.
 *  - Se excluye el cierre `excluirCierreId` (el que se está calculando).
 *  - Los cierres INCOMPLETOS (state processing/error) no sacan gastos del pool:
 *    sus filas no le cobraron a nadie.
 *  - Filas de cierres borrados que quedaron (fantasmas) SÍ cuentan: sus payments
 *    pudieron cobrarse; mejor no re-liquidar (lado seguro).
 *
 * @param {{ cierres?: Array<{id:string, state?:string, liquidatedExpenseIds?:Array}>,
 *           filasAplicadas?: Array<{montlyaccountId?:string, expenseId?:any}>,
 *           excluirCierreId?: string }} params
 * @returns {Set<any>}
 */
export function gastosYaLiquidados({ cierres = [], filasAplicadas = [], excluirCierreId = null } = {}) {
  const incompletos = new Set();
  const liquidados = new Set();
  for (const c of cierres) {
    if (c?.state === "processing" || c?.state === "error") {
      incompletos.add(c.id);
      continue;
    }
    if (c?.id === excluirCierreId) continue;
    (c?.liquidatedExpenseIds || []).forEach((id) => liquidados.add(id));
  }
  for (const f of filasAplicadas) {
    if (!f?.expenseId) continue;
    if (f.montlyaccountId === excluirCierreId) continue;
    if (f.montlyaccountId && incompletos.has(f.montlyaccountId)) continue;
    liquidados.add(f.expenseId);
  }
  return liquidados;
}

/** Pertenencia a una ventana de corte: start < d <= end (inicio exclusivo, fin inclusivo). */
export function isInWindow(date, start, end) {
  const d = isoOf(date);
  if (!d) return false;
  const s = String(start).slice(0, 10);
  const e = String(end).slice(0, 10);
  return d > s && d <= e;
}

/** Fecha anterior a la ventana: d <= start (el borde start pertenece al período previo). */
export function isBeforeWindow(date, start) {
  const d = isoOf(date);
  if (!d) return false;
  return d <= String(start).slice(0, 10);
}

/**
 * Vencimiento (maturity) sugerido para una ventana: `paymentDueDay` aplicado al MES de
 * `periodEnd` (independiente del corte). Si no hay paymentDueDay, usa el fin de ventana.
 * @returns {string} ISO "YYYY-MM-DD" o "" si la ventana es inválida.
 */
export function defaultMaturityFor(window, paymentDueDay) {
  if (!window || !window.end) return "";
  const endDate = toDate(window.end);
  if (!endDate) return "";
  const y = endDate.getFullYear();
  const m = endDate.getMonth() + 1;
  const due = Math.floor(Number(paymentDueDay));
  if (!paymentDueDay || isNaN(due) || due < 1) {
    return isoYMD(y, m, endDate.getDate());
  }
  return isoYMD(y, m, Math.min(due, lastDayOfMonth(y, m)));
}
